/**
 * Token manager singleton para armazenar token atual do Orul Auth.
 *
 * Este singleton é necessário porque o Orul Auth SDK gerencia tokens internamente
 * através de hooks React, mas o Axios interceptor precisa acessar o token de forma
 * síncrona para fazer requisições. O token é sincronizado pelo AuthProvider
 * quando disponível.
 *
 * O padrão singleton garante que há apenas uma instância compartilhada
 * entre todos os clientes da API.
 */
class OrulTokenManager {
  private static instance: OrulTokenManager

  private currentToken: string | null = null

  private expiresAt: number | null = null

  // eslint-disable-next-line no-useless-constructor, @typescript-eslint/no-empty-function
  private constructor() {}

  static getInstance(): OrulTokenManager {
    if (!OrulTokenManager.instance) {
      OrulTokenManager.instance = new OrulTokenManager()
    }
    return OrulTokenManager.instance
  }

  /**
   * Define o token e sua data de expiração
   * @param token Token de acesso
   * @param expiresAt Timestamp de expiração em milissegundos (opcional)
   */
  setToken(token: string | null, expiresAt?: number | null): void {
    this.currentToken = token
    this.expiresAt = expiresAt ?? null
  }

  /**
   * Obtém o token se ainda não estiver expirado
   * @returns Token de acesso ou null se expirado/não disponível
   */
  getToken(): string | null {
    if (!this.currentToken) {
      return null
    }

    // Validar expiração se expiresAt estiver disponível
    if (this.expiresAt !== null) {
      const now = Date.now()
      const timeUntilExpiry = this.expiresAt - now

      // Se expirou ou está prestes a expirar (menos de 1 minuto), retornar null
      if (timeUntilExpiry < 60000) {
        this.currentToken = null
        this.expiresAt = null
        return null
      }
    }

    return this.currentToken
  }

  clearToken(): void {
    this.currentToken = null
    this.expiresAt = null
  }
}

export const orulTokenManager = OrulTokenManager.getInstance()
